import React, { useEffect, useRef, useContext, useState, useCallback } from 'react';
import pdfIcon from '../../images/pdf.png';
import arpIcon from '../../images/arp-white.svg';
import arpUncontrolledIcon from '../../images/arp_uncontrolled.svg';
import heliportIcon from '../../images/heliport.svg';
import windArrowAd from '../../images/wind-arrow-ad.png';
import { AIS_URL, METARS_URL, ATIS_URL } from '../../data/config';
import { TrafficContext } from "../../App";

const Aerodromes = ({ map, openWindows, setOpenWindows, setWindowSize }) => {
  const clickHandlerRef = useRef(null);
  const traffic = useContext(TrafficContext);
  const [showOthersToggle, setShowOthersToggle] = useState(false);

  const [showUncontrolled, setShowUncontrolled] = useState(
    localStorage.getItem("showUncontrolled") === "false" ? false : true
  );
  const [showHeliports, setShowHeliports] = useState(
    localStorage.getItem("showHeliports") === "false" ? false : true
  );
  const [isVisible, setIsVisible] = useState(
    localStorage.getItem("aerodromesVisible") === "false" ? false : true
  );

  const windCorrections = {
    'EFHK': 0, 'EFET': 55, 'EFHA': 4, 'EFIV': 43,
    'EFJO': -20, 'EFJY': -44, 'EFKE': 82, 'EFKI': 10,
    'EFKK': 79, 'EFKS': -42, 'EFKT': -76, 'EFKU': -67,
    'EFLP': 23, 'EFMA': 67, 'EFMI': -23, 'EFOU': -31,
    'EFPO': -36, 'EFRO': 53, 'EFSA': -36, 'EFSI': -46,
    'EFTP': 26, 'EFTU': 5, 'EFUT': 12, 'EFVA': -73
  };

  const toggleVisibility = () => {
    const newVisibility = !isVisible;
    setIsVisible(newVisibility);
    localStorage.setItem("aerodromesVisible", newVisibility.toString()); // Save state
  };

  // Toggle uncontrolled and heliports visibility
  const updateAerodromeFilter = useCallback((uncontrolled, heliports) => {
    if (!map) return;
  
    let filters = ["all"];
  
    if (!uncontrolled) {
      filters.push([
        "any",
        ["!=", "Service", "Uncontrolled"], // Hide non-heliports
        ["==", "Service", "Heliport"], // Ensure heliports remain visible
      ]);
    }
  
    // If heliports is false, hide heliports separately
    if (!heliports) {
      filters.push(["!=", "Service", "Heliport"]);
    }
  
    // Apply the filter to the map
    map.setFilter("aerodromes3-aaopnp", filters);
  }, [map]);
  
  
  const toggleUncontrolled = useCallback(() => {
    setShowUncontrolled((prev) => {
      const newState = !prev;
      localStorage.setItem("showUncontrolled", newState.toString());
      updateAerodromeFilter(newState, showHeliports);
      return newState;
    });
  }, [showHeliports, updateAerodromeFilter]);
  
  const toggleHeliports = useCallback(() => {
    setShowHeliports((prev) => {
      const newState = !prev;
      localStorage.setItem("showHeliports", newState.toString());
      updateAerodromeFilter(showUncontrolled, newState);
      return newState;
    });
  }, [showUncontrolled, updateAerodromeFilter]);  

  
  const fetchAerodromeCharts = async (icao) => {
    try {
      const response = await fetch(AIS_URL + icao);
      const data = await response.json();
      return data.charts || [];
    } catch (error) {
      console.error(`Error fetching charts for ${icao}:`, error);
      return [];
    }
  };

  const fetchMetarData = async (icao) => {
    try {
      const response = await fetch(METARS_URL, {
        cache: 'no-store', // This tells the browser not to cache the response
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      const data = await response.json();
      return data.find(metar => metar.icao === icao) || null;
    } catch (error) {
      console.error(`Error fetching METAR data for ${icao}:`, error);
      return null;
    }
  };

  const countTraffic = (icao) => {
    if (!traffic || !Array.isArray(traffic)) return { departures: 0, arrivals: 0 };

    const departures = traffic.filter(pilot =>
      pilot.flight_plan?.departure === icao
    ).length;

    const arrivals = traffic.filter(pilot =>
      pilot.flight_plan?.arrival === icao
    ).length;

    return { departures, arrivals };
  };

  useEffect(() => {
    if (!map) return;
  
    updateAerodromeFilter(showUncontrolled, showHeliports);
  
    // Set visibility
    const layerId = "aerodromes3-aaopnp";
    map.setLayoutProperty(layerId, "visibility", isVisible ? "visible" : "none");
  }, [map, showUncontrolled, showHeliports, isVisible]);

  useEffect(() => {
    if (!map) return;

    if (clickHandlerRef.current) {
      map.off('click', 'aerodromes3-aaopnp', clickHandlerRef.current);
    }

    const clickHandler = async (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: ['aerodromes3-aaopnp'] });
      if (!features.length) return;
    
      const { Name: name = 'Unknown', ICAO: icao = '', Service: service = '', Controlled: controlled = true, rmk: rmk = '' } = features[0].properties;
      if (openWindows.some(win => win.title === icao)) return;
    
      // Show loading window immediately
      const windowId = `aerodrome-${name}-${Date.now()}`;
      setOpenWindows(prev => [
        ...prev,
        {
          id: windowId,
          title: icao,
          content: <div className="p-2 monospace-font text-sm">Loading...</div>
        }
      ]);
      setWindowSize(2);
    
      const charts = controlled ? await fetchAerodromeCharts(icao) : [];
      const { departures, arrivals } = countTraffic(icao);
      const metarData = controlled ? await fetchMetarData(icao) : null;
      let correctedWindDirection = 0;
      // wind direction correction
      if (metarData) {
        correctedWindDirection = metarData.windDir + windCorrections[icao];
      }
    
      // Update the window content with fetched data
      setOpenWindows(prev =>
        prev.map(win =>
          win.id === windowId
            ? {
                ...win,
                content: (
                  <div className="p-2 monospace-font text-sm">
                    <div className="flex">
                      <div className="aerodromeTitle flex-1">{icao} - {name}</div>
                    </div>
    
                    <div className="flex items-center" style={{alignItems: "stretch"}}>
                      <div className="aerodromedeparr flex-1 mr-1">
                        {departures} <span className="text-xs">{departures === 1 ? 'DEPARTURE' : 'DEPARTURES'}</span>
                      </div>
                      <div className="aerodromedeparr flex-1 ml-1">
                        {arrivals} <span className="text-xs">{arrivals === 1 ? 'ARRIVAL' : 'ARRIVALS'}</span>
                      </div>
                    </div>
    
                    <p className='p-2 text-xs'>{service}</p>
    
                    {service === "Heliport" ? (
                      <p className="p-2 pt-0"> {rmk.split("\n").map((line, index) => (
                        <span key={index}>
                          {line}
                          <br/><br/>
                        </span>
                      ))}
                      </p>
                    ) : controlled ? (
                      <>
                        {metarData && (
                          <>
                            <p className='pilotCallsign pilotDetails mb-2'>{metarData.metar}</p>
                            <div className="relative w-full max-w-[700px]">
                              <img src={`./airports/${icao.toLowerCase()}.png`} alt={icao} className="w-full" />
                              {metarData && (
                                <div className="absolute inset-0 flex justify-center items-center">
                                  <div className={`flex items-center rounded-full p-2 ${icao.toLowerCase() === "efhk" ? "bg-gray-600" : ""}`}>
                                    <div className="pr-1 text-sm text-white" style={{ lineHeight: "1.2" }}>
                                      {metarData.windSpeed}kt
                                    </div>
                                    <img src={windArrowAd} alt="WIND" className="ml-2 w-[15px]" style={{ transform: `rotate(${correctedWindDirection}deg)` }}/>
                                    <div className="pl-3 text-sm text-white">
                                      {metarData.visibility === "16000" ? "CAVOK"
                                        : metarData.visibility === "10000" ? "10km"
                                        : `${metarData.visibility}m`}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <p className="p-2 pt-0">
                        More information at{' '}
                        <a
                          href={`https://lentopaikat.fi/${icao.toLowerCase()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline text-blue-500"
                        >
                          lentopaikat.fi/{icao.toLowerCase()}
                        </a>
                      </p>
                    )}
    
                    {controlled !== false && charts.length > 0 ? (
                      <table className="min-w-full mt-3 p-2">
                        <thead>
                          <tr className="border-none">
                            <th className="p-2 text-s text-left">CHARTS AIP FINLAND</th>
                          </tr>
                        </thead>
                        <tbody style={{backgroundColor: "#F0F0F0"}}>
                            {charts.map(({ chart, link, status }, index) => (
                                <ChartRow key={index} chart={chart} link={link} status={status} />
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      controlled !== false && (
                        <p className="text-red-500 mt-2 p-2">No charts available.</p>
                      )
                    )}
                  </div>
                )
              }
              : win
        )
      );
    };
    

    clickHandlerRef.current = clickHandler;

    map.on('click', 'aerodromes3-aaopnp', clickHandler);

    map.on('mouseenter', 'aerodromes3-aaopnp', () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'aerodromes3-aaopnp', () => {
      map.getCanvas().style.cursor = '';
    });

    return () => map.off('click', 'aerodromes3-aaopnp', clickHandler);
  }, [map, openWindows, setOpenWindows]);

  // set visibility of ARPs
  useEffect(() => {
    if (!map) return;

    const layerId = "aerodromes3-aaopnp";

    // Cleanup
    return () => {
      if (map.isStyleLoaded()) {
        map.setLayoutProperty(layerId, "visibility", "none");
      }
    };
  }, [map]);

  return (
    <div 
      onMouseEnter={() => setShowOthersToggle(true)}
      onMouseLeave={() => setShowOthersToggle(false)}
      style={{width: "106px", backgroundColor: "transparent"}}
    >

      <button
        data-tooltip-id="map-buttons-tooltip"
        data-tooltip-content=""
        className={`w-8 h-8 ${isVisible ? "bg-gray-600" : "bg-gray-400"} text-white flex items-center justify-center`}
        onClick={toggleVisibility}
        
      >
        <img className="w-5" src={arpIcon} alt="Traffic" />
      </button>

      {isVisible && showOthersToggle && (
        <button
          data-tooltip-id="map-buttons-tooltip"
          data-tooltip-content=""
          className={`w-8 h-8 ${showUncontrolled ? "bg-gray-600" : "bg-gray-400"} text-white flex items-center justify-center`}
          onClick={toggleUncontrolled}
          style={{position: "absolute", top: "34px", left: "40px"}}
        >
          <img className="w-3" src={arpUncontrolledIcon} alt="Heliport" />
        </button>
      )}

      {isVisible && showOthersToggle && (
        <button
          data-tooltip-id="map-buttons-tooltip"
          data-tooltip-content=""
          className={`w-8 h-8 ${showHeliports ? "bg-gray-600" : "bg-gray-400"} text-white flex items-center justify-center`}
          onClick={toggleHeliports}
          style={{position: "absolute", top: "34px", left: "75px"}}
        >
          <img className="w-4" src={heliportIcon} alt="Heliport" />
        </button>
      )}

    </div>
  );
};

const ChartRow = ({ chart, link, status }) => {
    const isRemoved = status === "removed";
    const isAdded = status === "added";
    const isDefault = status === "default";
    const clickable = !isRemoved;

    return (
        <tr className="border-t">
            <td
                className={`p-2 ${
                    clickable
                        ? "cursor-pointer hover:underline"
                        : "text-gray-500 select-none"
                } ${
                    isAdded
                        ? "text-green-700"
                        : isRemoved
                            ? "text-red-500"
                            : ""
                }`}
                onClick={() =>
                    clickable && window.open(link, "_blank", "width=800,height=600")
                }
            >
                <span className="inline-flex items-center gap-3">
                  <img className="w-3" src={pdfIcon} alt="PDF" />
                    {chart}{" "}
                    {isAdded && "(new)"}
                    {isRemoved && "(withdrawn)"}
                </span>

            </td>
        </tr>
    );
};

export default Aerodromes;
